/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo } from 'react'
import { Controller, useFormContext, get } from 'react-hook-form'

import { InputLabel, FormControl } from '@material-ui/core'
import { FormHelperText, TextField, InputAdornment } from '@mui/material'

import PropTypes from 'prop-types'
import classNames from 'classnames'

import generalInputStyles from '../styles'
import { defaultInputRules } from '../constants'
import { HelpTooltipButton } from '_/components/help-tooltip-button'

/**
 * Basic input component that uses Material-UI TextField and React Hook Form Controller.
 * @param {Object} props - The props object.
 * @param {string} props.label - The label of the input.
 * @param {string} props.name - The name of the input. (Hook-Form related)
 * @param {boolean} [props.required=false] - Whether the input is required or not.
 * @param {string} [props.defaultValue=''] - The default value of the input.
 * @param {Object} [props.rules={}] - The validation rules for the input. Follows the same rules as React Hook Form.
 * @param {Object} [props.style={}] - The style object to be applied to the component.
 * @param {string} [props.containerClasses] - The classes to be applied to the container element.
 * @param {string} [props.labelClasses] - The classes to be applied to the label element.
 * @returns {JSX.Element} - The BasicInput component.
 */
function BasicInput({
  label,
  name,
  required = false,
  showLabelSpan = true,
  defaultValue = '',
  rules = {},
  style = {},
  containerClasses,
  labelClasses = null,
  showErrorMessage = true,
  customErrorMessage = '',
  textArea = false,
  inputSx = {},
  helpText = '',
  ...rest
}) {
  const generalStyles = generalInputStyles()

  const inputRules = useMemo(() => {
    const formattedRules = Object.entries(rules).reduce((acc, [rule, value]) => {
      if (rule === 'validate' && typeof value === 'object') {
        return { ...acc, validate: value }
      }

      if (typeof value === 'object') {
        if (!Object.values(value)?.length) return acc
        if (value?.message) return { ...acc, [rule]: value }
      }

      if (defaultInputRules[rule]) {
        return { ...acc, [rule]: defaultInputRules[rule]({ value }) }
      }

      return acc
    }, {})

    if (required && !formattedRules.required) {
      return { ...formattedRules, required: defaultInputRules.required() }
    }

    return formattedRules
  }, [rules, required])

  const {
    formState: { errors },
    control,
    setValue,
  } = useFormContext()

  const errorMessage = get(errors, `${name}.message`) ?? customErrorMessage

  useEffect(() => {
    setValue(name, defaultValue)
  }, [defaultValue, name, setValue])

  return (
    <FormControl
      style={{ ...style }}
      className={classNames(generalStyles.formControl, containerClasses ?? null)}
    >
      <InputLabel
        variant="standard"
        id={`${name}-label`}
        disabled={rest.disabled}
        style={{ color: errorMessage ? '#C90000' : null, top: '-39px' }}
        className={classNames(
          textArea ? generalStyles.inputLabelTextArea : generalStyles.inputLabel,
          labelClasses ?? null
        )}
      >
        {label}
        {required && showLabelSpan && <span className={generalStyles.required}>*</span>}
      </InputLabel>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        rules={inputRules}
        as={
          <TextField
            id={name}
            variant="standard"
            defaultValue={defaultValue}
            error={!!errorMessage}
            required={required}
            InputProps={{
              style: {
                fontSize: 16,
                borderRadius: '8px',
                border: `${textArea ? '1px' : 0} solid ${errorMessage ? '#C90000' : '#717171'}`,
              },
              maxLength: rules?.maxLength?.value || rules?.maxLength || 255,
              endAdornment: helpText && (
                <InputAdornment position="end">
                  <HelpTooltipButton text={helpText} />
                </InputAdornment>
              ),
            }}
            inputProps={{
              maxLength: rules?.maxLength?.value || rules?.maxLength || 255,
              'data-field-path': name,
            }}
            sx={inputSx}
            className={[textArea && generalStyles.textArea]}
            {...rest}
          />
        }
      />
      {showErrorMessage && (
        <FormHelperText className={generalStyles.errorMessage} error={!!errorMessage}>
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  )
}

BasicInput.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  showErrorMessage: PropTypes.bool,
  defaultValue: PropTypes.string,
  rules: PropTypes.instanceOf(Object),
  style: PropTypes.instanceOf(Object),
  containerClasses: PropTypes.string,
  labelClasses: PropTypes.string,
}

BasicInput.defaultProps = {
  required: false,
  showErrorMessage: true,
  defaultValue: '',
  rules: {},
  style: {},
  containerClasses: null,
  labelClasses: null,
}

export default BasicInput
